import { SettingsNavigation } from '@studio/navigation';
import { Assistant, AssistantSession, MessageType } from '@studio/assistant';
import { Icon, NavigationMenu, toast } from '@appcues/sonar';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AssistantSessionSortKey,
  ContentSortKey,
  ContentState,
  SortDirection,
  User,
} from '@studio/graphql-types';
import { useFeature } from 'studio5/hooks';
import { useAgentMemory } from 'studio5/hooks/use-agent-memory';
import useDebounce from 'studio5/hooks/use-debounce';
import { useAuth, useGraphQLMutation, useGraphQLQuery } from 'studio5/contexts';
import {
  ASSISTANT_SESSIONS,
  DELETE_ASSISTANT_SESSION,
  GET_ASSISTANT_SESSION_MESSAGES,
  GET_CONTENTS,
  GET_USER_ACCOUNT,
} from 'studio5/components/Navigation/queries';
import {
  AssistantSessionsQuery,
  AssistantSessionsQueryVariables,
  DeleteAssistantSessionMutation,
  DeleteAssistantSessionMutationVariables,
  GetAssistantSessionMessagesQuery,
  GetAssistantSessionMessagesQueryVariables,
  GetContentsQuery,
  GetContentsQueryVariables,
  GetUserAccountQuery,
} from 'studio5/components/Navigation/__generated__/queries.types';
import { useEntitlement } from 'studio5/hooks/use-entitlement';
import { ENTITLEMENTS } from 'studio5/constants/entitlements';
import { AssistantButton } from 'studio5/components/Navigation/NavigationProvider.styled';

const SettingsNavigationProvider = () => {
  const [hasLocalizationAccess] = useEntitlement(
    ENTITLEMENTS.LOCALIZATION_ACCESS
  );
  const hasIntegrationPageRedesign = useFeature(
    'INTEGRATION_LISTING_PAGE_REDESIGN'
  );
  const [hasFlows2Access] = useEntitlement(ENTITLEMENTS.FLOWS_2_ACCESS);
  const hasFlows2GaRollout = useFeature('FLOWS_2_GA_ROLLOUT');

  const hasOpenBuilderFlows = hasFlows2Access && !hasFlows2GaRollout;
  const hasMobileFlows = useFeature('MOBILE_FLOWS');
  const hasAIAssistant = useFeature('AI_ASSISTANT');
  const hasAssistantSessions = useFeature('ASSISTANT_SESSIONS');
  const hasSubscription = useFeature('SUBSCRIPTION');
  const hasLegacyFlowsAccess = useFeature('LEGACY_FLOWS_ACCESS', true);
  const hasAgentStudio = useFeature('AGENT_STUDIO');

  const navigationFeatureFlags = useMemo(
    () => ({
      hasIntegrationPageRedesign,
      hasOpenBuilderFlows,
      hasMobileFlows,
      hasAIAssistant,
      hasSubscription,
      hasLegacyFlowsAccess,
    }),
    [
      hasIntegrationPageRedesign,
      hasOpenBuilderFlows,
      hasMobileFlows,
      hasAIAssistant,
      hasSubscription,
      hasLegacyFlowsAccess,
    ]
  );

  const { memoryContent, memoryUpdatedAt, memoryFetching, fetchMemory } =
    useAgentMemory();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [{ data }] = useGraphQLQuery<GetUserAccountQuery>({
    query: GET_USER_ACCOUNT,
  });
  const { user } = data?.identity || {};
  const { isSpoofing, spoof, logout, client } = useAuth();
  const [token, setToken] = useState<string>();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [contentsSearch, setContentsSearch] = useState<string>('');
  const debouncedContentsSearch = useDebounce(contentsSearch, 300);

  useEffect(() => {
    client.getToken().then(setToken);
  }, [client]);

  const [
    { fetching: fetchingAssistantSessions, data: assistantSessionsData },
    fetchAssistantSessions,
  ] = useGraphQLQuery<AssistantSessionsQuery, AssistantSessionsQueryVariables>({
    query: ASSISTANT_SESSIONS,
    variables: {
      sort: {
        key: AssistantSessionSortKey.UpdatedAt,
        direction: SortDirection.Desc,
      },
    },
    requestPolicy: 'cache-and-network',
  });

  const [{ data: accountData }] = useGraphQLQuery<GetUserAccountQuery>({
    query: GET_USER_ACCOUNT,
  });
  const { account } = accountData?.identity || {};
  const hasCaptainAiAnalytics = account?.aiAllowEndUserData;

  const [
    {
      fetching: fetchingAssistantSessionMessages,
      data: assistantSessionMessagesData,
    },
    fetchAssistantSessionMessages,
  ] = useGraphQLQuery<
    GetAssistantSessionMessagesQuery,
    GetAssistantSessionMessagesQueryVariables
  >({
    query: GET_ASSISTANT_SESSION_MESSAGES,
    requestPolicy: 'network-only',
    pause: !sessionId,
    variables: {
      id: sessionId ?? '',
    },
  });

  const [{ data: contentsData }] = useGraphQLQuery<
    GetContentsQuery,
    GetContentsQueryVariables
  >({
    query: GET_CONTENTS,
    variables: {
      filter: {
        term: debouncedContentsSearch,
        states: [ContentState.Draft, ContentState.Published],
      },
      pageInfo: {
        limit: 1000,
        offset: 0,
      },
      sort: [
        {
          direction: SortDirection.Asc,
          key: ContentSortKey.Name,
        },
      ],
    },
  });

  const onContentSearch = useCallback(
    (search: string) => {
      setContentsSearch(search);
    },
    [setContentsSearch]
  );

  const handleSessionSelect = (id: string) => {
    setSessionId(id);
    fetchAssistantSessionMessages();
  };

  const [, deleteSession] = useGraphQLMutation<
    DeleteAssistantSessionMutation,
    DeleteAssistantSessionMutationVariables
  >(DELETE_ASSISTANT_SESSION);

  const handleDeleteSession = async (id: string) => {
    try {
      const result = await deleteSession({ id });
      if (result.data?.deleteAssistantSession?.success) {
        fetchAssistantSessions();
        toast.success('Session deleted');
      } else {
        toast.danger('Failed to delete session');
      }
    } catch (error) {
      console.error(error);
      toast.danger('Failed to delete session');
    }
  };

  const sessions = assistantSessionsData?.assistantSessions?.sessions || [];

  return (
    <SettingsNavigation
      NavLink={NavLink}
      featureFlags={navigationFeatureFlags}
      hasLocalizationAccess={hasLocalizationAccess}
      onClickBackArrow={() => navigate('/')}
      currentPath={pathname}
      user={user as User}
      onSpoof={spoof}
      isSpoofing={isSpoofing}
      onClickLogout={logout}
      actions={
        hasAIAssistant ? (
          <Assistant
            contents={contentsData?.contents}
            onContentSearch={onContentSearch}
            user={{
              fullName: user?.fullname ?? '',
              avatarUrl: user?.avatarUrl ?? '',
            }}
            jwt={token ?? ''}
            url={import.meta.env.CUSTOMER_API_URL ?? ''}
            sessions={sessions as AssistantSession[]}
            fetchingSessions={fetchingAssistantSessions}
            getSessionMessages={handleSessionSelect}
            refreshSession={() => {
              setSessionId(null);
              fetchAssistantSessions();
            }}
            defaultMessages={
              (assistantSessionMessagesData?.assistantSession?.messages ||
                []) as MessageType[]
            }
            fetchingAssistantSessionMessages={fetchingAssistantSessionMessages}
            hasAssistantSessions={Boolean(hasAssistantSessions)}
            hasAnalytics={Boolean(hasCaptainAiAnalytics)}
            onDeleteSession={handleDeleteSession}
            config={{
              mobileBuilderUrl: import.meta.env.MOBILE_BUILDER_URL,
              appcuesAppAccess: hasAgentStudio ?? undefined,
            }}
            memoryContent={memoryContent}
            memoryUpdatedAt={memoryUpdatedAt}
            memoryFetching={memoryFetching}
            onFetchMemory={fetchMemory}
            settingsUrl="/settings/ai-assistant"
          >
            <NavigationMenu.Item>
              <AssistantButton>
                <Icon icon={faSparkles} colorToken="foreground-link" />
                Captain AI
              </AssistantButton>
            </NavigationMenu.Item>
          </Assistant>
        ) : null
      }
    />
  );
};

export default SettingsNavigationProvider;
